// The playground's client (LH-F5, LH-B2). Two ways, one interface (so the tests can plug in an in-process agent):
//   fixtures (npm run dev): firstfund-lender-hub/agent on :3029, which runs one hub-sandbox Node container per batch
//   the API (npm run dev:api): the same routes in the Lender Hub API under /admin/v1/sandboxes, with the session; the
//     API seeds the sandbox from the batch's git branch and writes .env from the vault, so the browser sends neither
// Nothing here carries applicant data: the inputs are the demo test applicants.
import { api, ApiError, apiMode } from '@/lib/hubApi'
import type { Manifest, TestEvent, TestResult } from '@/types'

export const AGENT_URL: string = (import.meta.env.VITE_AGENT_URL as string | undefined) ?? 'http://127.0.0.1:3029'

export interface TreeEntry { path: string; kind: 'file' | 'folder'; closed?: boolean; count?: number; size?: number }
export interface SandboxInfo { id: string; lender: string; version: string; runtime: 'docker' | 'local'; container: string | null; startedAt: number | null; environment: string; seeded?: boolean; /** the working copy came from another copy of the Hub and has unsaved work: Save or Re-seed */ stale?: boolean; seed?: string | null }
export interface OpenRequest { lender: string; version: string; files?: { path: string; content: string }[]; env?: Record<string, string>; environment?: 'uat' | 'production'; author?: { name: string; email: string }; limits?: { memoryMb?: number; cpus?: number }; reset?: boolean; /** the Hub copy the files come from (the batch's commit) */ seed?: string }
export interface RunRequest { file: string; input: Record<string, unknown>; prev?: Record<string, unknown> | null; reply?: { status: number; body: unknown } | null; mode?: 'sample' | 'real'; environment?: 'uat' | 'production'; timeoutMs?: number }
/** hub-run's result (firstfund-lender-hub/sdk) */
export interface RunResult {
  ok: boolean
  outcome: string | null
  value?: unknown
  error: string | null
  issues: { level: 'error' | 'warning'; text: string }[]
  calls: { call: { method: string; url: string; headers?: Record<string, string>; body?: unknown; timeoutMs?: number }; reply: { status: number; body: unknown; headers?: Record<string, string>; ms: number; error?: string }; issues: { level: 'error' | 'warning'; text: string }[] }[]
  log: string[]
  envMissing: string[]
  ms: number
  stderr?: string
}
/** PG-2a · one request of the HTTP client, as hub-http sends it from the sandbox */
export interface HttpRequest { method: string; url: string; headers: [string, string][]; body: string | null; bodyEncoding?: 'utf8' | 'base64'; timeoutMs?: number; followRedirects?: boolean; /** PG-2b: multipart parts */ form?: HttpFormPart[] | null }
/** a multipart part: a text field, or a file as base64 */
export type HttpFormPart = { name: string; value: string } | { name: string; filename: string; contentType: string; base64: string }
/** hub-http's answer (+ where it went out from) */
export interface HttpAnswer {
  ok: boolean
  status?: number
  statusText?: string
  httpVersion?: string
  headers?: [string, string][]
  body?: string
  bodyEncoding?: 'utf8' | 'base64'
  size?: number
  truncated?: boolean
  ms: number
  ttfbMs?: number
  remoteAddress?: string | null
  url?: string
  redirects: { status: number; url: string }[]
  error?: string | null
  code?: string | null
  sentFrom?: string
  runtime?: 'docker' | 'local'
  /** PG-2b: what really went out first ({{secrets}} masked) */
  sent?: { method: string; url: string; headers: [string, string][]; bodyBytes: number }
  /** PG-2b: {{VARIABLES}} with no value (code EVAR) */
  missing?: string[]
}
export interface GitCommit { sha: string; message: string; who: string; at: string; files?: string[]; nothing?: boolean }

export interface AgentClient {
  health(): Promise<{ ok: boolean; runtime: 'docker' | 'local'; note: string | null }>
  open(req: OpenRequest): Promise<SandboxInfo>
  close(id: string): Promise<void>
  files(id: string): Promise<TreeEntry[]>
  read(id: string, path: string): Promise<string>
  write(id: string, path: string, content: string): Promise<void>
  remove(id: string, path: string): Promise<void>
  rename(id: string, from: string, to: string): Promise<void>
  mkdir(id: string, path: string): Promise<void>
  env(id: string, vars: Record<string, string>, environment: 'uat' | 'production'): Promise<void>
  run(id: string, req: RunRequest): Promise<RunResult>
  /** PG-2a: one HTTP request sent from the sandbox (the HTTP client) */
  http(id: string, req: HttpRequest): Promise<HttpAnswer>
  discover(id: string): Promise<Manifest>
  git(id: string, op: 'status' | 'log' | 'commit' | 'diff', body?: { message?: string; author?: { name: string; email: string } }): Promise<{ changes?: { mark: string; path: string }[]; commits?: GitCommit[]; stat?: string } & Partial<GitCommit>>
  exec(id: string, cmd: string[], timeoutMs?: number): Promise<{ code: number; stdout: string; stderr: string }>
  /** the WebSocket address of a shell in the sandbox (null when there is none, e.g. in tests) */
  terminalUrl(id: string, cols: number, rows: number): string | null
}

export const sandboxId = (lender: string, version: string) => `${lender}@${version}`

export class AgentOffline extends Error {
  constructor(message = `The playground agent is not running at ${AGENT_URL}. In firstfund-lender-hub/agent run: npm run dev (Docker optional).`) {
    super(message)
    this.name = 'AgentOffline'
  }
}

/** What to start when the playground does not answer, in the words of the current mode. */
export const agentStartHint = () =>
  apiMode()
    ? 'The playground runs in the Lender Hub API: Docker Desktop on and the sandbox image built (in firstfund-lender-hub/api2: npm run sandbox:build), then restart the API.'
    : `Start the playground agent: cd firstfund-lender-hub/agent && npm run dev (${AGENT_URL}); Docker Desktop + npm run sandbox:build for the real container.`

export function httpAgent(base = AGENT_URL): AgentClient {
  const call = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
    let res: Response
    try {
      res = await fetch(`${base}${path}`, { method, headers: body !== undefined ? { 'content-type': 'application/json' } : undefined, body: body !== undefined ? JSON.stringify(body) : undefined })
    } catch {
      throw new AgentOffline()
    }
    const text = await res.text()
    const data = text ? (JSON.parse(text) as T & { error?: string }) : ({} as T & { error?: string })
    if (!res.ok) throw new Error(data.error ?? `${method} ${path}: ${res.status}`)
    return data
  }
  const q = (path: string) => encodeURIComponent(path)
  return {
    health: () => call('GET', '/health'),
    open: (req) => call('POST', '/sandboxes', req),
    close: (id) => call('DELETE', `/sandboxes/${q(id)}`),
    files: async (id) => (await call<{ files: TreeEntry[] }>('GET', `/sandboxes/${q(id)}/files`)).files,
    read: async (id, path) => (await call<{ content: string }>('GET', `/sandboxes/${q(id)}/file?path=${q(path)}`)).content,
    write: (id, path, content) => call('PUT', `/sandboxes/${q(id)}/file`, { path, content }),
    remove: (id, path) => call('DELETE', `/sandboxes/${q(id)}/file?path=${q(path)}`),
    rename: (id, from, to) => call('POST', `/sandboxes/${q(id)}/rename`, { from, to }),
    mkdir: (id, path) => call('POST', `/sandboxes/${q(id)}/mkdir`, { path }),
    env: (id, vars, environment) => call('PUT', `/sandboxes/${q(id)}/env`, { vars, environment }),
    run: (id, req) => call('POST', `/sandboxes/${q(id)}/run`, req),
    http: (id, req) => call('POST', `/sandboxes/${q(id)}/http`, req),
    discover: async (id) => ({ ...(await call<Omit<Manifest, 'source'>>('POST', `/sandboxes/${q(id)}/discover`)), source: 'agent' as const }),
    git: (id, op, body) => call('POST', `/sandboxes/${q(id)}/git`, { op, ...(body ?? {}) }),
    exec: (id, cmd, timeoutMs) => call('POST', `/sandboxes/${q(id)}/exec`, { cmd, timeoutMs }),
    terminalUrl: (id, cols, rows) => `${base.replace(/^http/, 'ws')}/sandboxes/${q(id)}/terminal?cols=${cols}&rows=${rows}`,
  }
}

/**
 * The playground in the Lender Hub API (LH-B2): the agent's routes under /admin/v1/sandboxes with the session and the
 * CSRF token. open sends only the batch (lender, version), the environment and reset; env sends only the environment.
 */
export function apiAgent(): AgentClient {
  const call = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
    try {
      return await api<T>(method, path, body)
    } catch (err) {
      if (err instanceof ApiError && (err.status === 0 || err.error === 'sandbox_offline')) throw new AgentOffline(err.message)
      throw err
    }
  }
  const q = (path: string) => encodeURIComponent(path)
  const s = (id: string) => `/sandboxes/${q(id)}`
  return {
    health: () => call('GET', '/playground/health'),
    open: (req) => call('POST', '/sandboxes', { lender: req.lender, version: req.version, environment: req.environment ?? 'uat', reset: req.reset ?? false }),
    close: (id) => call('DELETE', s(id)),
    files: async (id) => (await call<{ files: TreeEntry[] }>('GET', `${s(id)}/files`)).files,
    read: async (id, path) => (await call<{ content: string }>('GET', `${s(id)}/file?path=${q(path)}`)).content,
    write: (id, path, content) => call('PUT', `${s(id)}/file`, { path, content }),
    remove: (id, path) => call('DELETE', `${s(id)}/file?path=${q(path)}`),
    rename: (id, from, to) => call('POST', `${s(id)}/rename`, { from, to }),
    mkdir: (id, path) => call('POST', `${s(id)}/mkdir`, { path }),
    env: (id, _vars, environment) => call('PUT', `${s(id)}/env`, { environment }),
    run: (id, req) => call('POST', `${s(id)}/run`, req),
    http: (id, req) => call('POST', `${s(id)}/http`, req),
    discover: async (id) => ({ ...(await call<Omit<Manifest, 'source'>>('POST', `${s(id)}/discover`)), source: 'agent' as const }),
    git: (id, op, body) => call('POST', `${s(id)}/git`, { op, ...(body ?? {}) }),
    exec: (id, cmd, timeoutMs) => call('POST', `${s(id)}/exec`, { cmd, timeoutMs }),
    terminalUrl: (id, cols, rows) => (typeof location === 'undefined' ? null : `${location.origin.replace(/^http/, 'ws')}${String(import.meta.env.VITE_HUB_API_BASE ?? '')}/admin/v1${s(id)}/terminal?cols=${cols}&rows=${rows}`),
  }
}

let current: AgentClient | null = null
let byMode: { api: AgentClient; agent: AgentClient } | null = null
/** The playground client of the current mode (or the one the tests plugged in). */
export const getAgent = (): AgentClient => {
  if (current) return current
  byMode ??= { api: apiAgent(), agent: httpAgent() }
  return apiMode() ? byMode.api : byMode.agent
}
/** the tests plug in an in-process agent (null = back to the mode's own) */
export const setAgent = (a: AgentClient | null) => { current = a }

/** Runs one test event through the sandbox: the endpoint with the saved reply injected; green = right outcome and a clean system check. */
export async function runTestEvent(agent: AgentClient, sb: string, file: string, test: TestEvent, input: Record<string, unknown>, opts: { prev?: Record<string, unknown>; mode?: 'sample' | 'real'; environment?: 'uat' | 'production' } = {}): Promise<TestResult> {
  const r = await agent.run(sb, { file, input, prev: opts.prev ?? null, reply: test.reply, mode: opts.mode ?? 'sample', environment: opts.environment ?? 'uat' })
  const blocked = r.issues.filter((i) => i.level === 'error')
  return {
    testId: test.id,
    endpoint: test.endpoint,
    name: test.name,
    expectedOutcome: test.expectedOutcome,
    actualOutcome: r.outcome,
    ok: r.ok && r.outcome === test.expectedOutcome,
    log: r.log,
    error: r.error ?? (blocked.length ? `Not approved: the output does not match the system (${blocked.map((i) => i.text).join('; ')})` : null),
    value: r.value,
    ms: r.ms,
    issues: r.issues,
    call: r.calls[0]?.call,
    reply: r.calls[0]?.reply,
    envMissing: r.envMissing,
  }
}
